/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable eqeqeq */
import React, { useMemo } from 'react';
import TextBubbles from '@home/components/TextBubbles';
import { channelInfo } from '@op/components/ChannelLabel';
import { getCountryFlag } from '@home/_config/countries';
import CompanyDefault from '@/pages/home/assets/img/common/company-default.svg';
import BasicImage from '@home/components/BasicImage';
import { Cell } from '@/pages/dashboard/components/CommonTable/components/CommonTableCell/Cell';
import type { IColumn } from '@/pages/dashboard/components/CommonTable/type';
import type {
  PrMediaCellRenderContext,
  TPlatformTotalRow,
  UsePrMediaTableColumnsParams,
} from '../_type';
import {
  DEFAULT_METRIC_FEATURES,
  METRIC_CODES,
  METRIC_NAME_CN,
  METRIC_NAME_EN,
  REGION_METRIC_CODES,
  REGION_METRIC_NAME_CN,
  REGION_METRIC_NAME_EN,
  STREAM_METRIC_CODES,
  STREAM_METRIC_NAME_CN,
  STREAM_METRIC_NAME_EN,
} from '../_constants';
import {
  columnOverrideMap,
  formatPostTimeForDisplay,
  formatStreamDurationMinutes,
  formatStreamDurationSeconds,
  formatValueForRule,
  getLogoUrl,
  mergeFeatures,
  normalizePlatformTotalsFromApi,
  resolveMediaAndCountryFlag,
  takeLabelEnCn,
} from '../_utils';
import classNames from 'classnames';
import { buildPrMetricColumns } from './usePrMetricColumns';

/** Posts / Videos：主行优先 `title`，空则回退 message / video_title */
function prCardPrimaryLine(title: unknown, fallback: unknown): string {
  if (title != null && String(title).trim() !== '') {
    return String(title);
  }
  return String(fallback ?? '');
}

export function usePrMediaTableColumns(
  p: UsePrMediaTableColumnsParams,
): IColumn[] {
  const {
    tableMode,
    isEN,
    colMaxes,
    channels,
    colorByCodeProp,
    cardLinkEnabled,
    cellRenderers,
    columnConfigFromPage,
    rowPage,
    pageSize,
    setStackedBarTooltip,
  } = p;

  return useMemo(() => {
    const ov = columnOverrideMap(columnConfigFromPage);
    const isRegions = tableMode === 'regions';

    const activeMetricCodes =
      tableMode === 'regions'
        ? REGION_METRIC_CODES
        : tableMode === 'streams'
          ? STREAM_METRIC_CODES
          : METRIC_CODES;
    const metricNameEnMap =
      tableMode === 'regions'
        ? (REGION_METRIC_NAME_EN as Record<string, string>)
        : tableMode === 'streams'
          ? (STREAM_METRIC_NAME_EN as Record<string, string>)
          : (METRIC_NAME_EN as Record<string, string>);
    const metricNameCnMap =
      tableMode === 'regions'
        ? (REGION_METRIC_NAME_CN as Record<string, string>)
        : tableMode === 'streams'
          ? (STREAM_METRIC_NAME_CN as Record<string, string>)
          : (METRIC_NAME_CN as Record<string, string>);

    const defaultMetricFeaturesBase: Record<string, unknown> = {
      format: DEFAULT_METRIC_FEATURES.format,
      sortable: DEFAULT_METRIC_FEATURES.sortable,
      downLoadFormat: DEFAULT_METRIC_FEATURES.downLoadFormat,
    };
    const getColumnFeaturesExtra = (columnCode: string): Record<string, unknown> => {
      const raw = ov.get(columnCode)?.features;
      if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
        return { ...raw };
      }
      return {};
    };
    const engagementFeatureMerged = mergeFeatures(
      defaultMetricFeaturesBase,
      getColumnFeaturesExtra('engagement'),
    );
    const channelValueFormat = (engagementFeatureMerged.format as string) || 'thousandsInit';

    // ── 行号列 ──
    const { nameEn: rowEn, nameCn: rowCn } = takeLabelEnCn(
      { nameEn: '#', nameCn: '#' },
      ov.get('row_index') as any,
    );
    const rowO = ov.get('row_index');
    const rowIndexCol: IColumn = {
      code: 'row_index',
      name: rowEn,
      nameEn: rowEn,
      nameCn: rowCn,
      align: (rowO?.align as IColumn['align']) || 'center',
      width: rowO?.width ?? 48,
      features: mergeFeatures({ sortable: false }, rowO?.features as Record<string, unknown> | undefined) as IColumn['features'],
      getValue: () => 0,
      render: (value, record, rowIndex) => {
        const ctx: PrMediaCellRenderContext = {
          value,
          record,
          rowIndex,
          isEN,
          channels,
          colMaxes: { ...colMaxes },
          formatMetric: (raw) => formatValueForRule(raw, 'thousandsInit'),
        };
        if (cellRenderers?.row_index) {
          return <Cell config={{ column: rowIndexCol }}>{cellRenderers.row_index(ctx)}</Cell>;
        }
        const n = (rowPage - 1) * pageSize + rowIndex + 1;
        return (
          <Cell
            className={classNames('dbh-pr-media-table__index-cell', {
              'dbh-pr-media-table__index-cell--articles':
                tableMode === 'articles' ||
                tableMode === 'posts' ||
                tableMode === 'videos' ||
                tableMode === 'streams',
            })}
            config={{ column: rowIndexCol }}
          >
            {n}
          </Cell>
        );
      },
    };

    let firstColumn: IColumn;

    const isCardMode =
      tableMode === 'articles' ||
      tableMode === 'posts' ||
      tableMode === 'videos' ||
      tableMode === 'streams';

    if (isCardMode) {
      const oFirstCard =
        (ov.get('date') as Partial<IColumn> | undefined) || ov.get('article_date');
      const { nameEn: aEn, nameCn: aCn } = takeLabelEnCn(
        { nameEn: 'Sort by Date', nameCn: '按日期排序' },
        oFirstCard,
      );
      const articleFeatures = mergeFeatures(
        { sortable: true },
        oFirstCard?.features as Record<string, unknown> | undefined,
      ) as IColumn['features'];
      const firstColCode = 'date';

      firstColumn = {
        code: firstColCode,
        name: aEn,
        nameEn: aEn,
        nameCn: aCn,
        align: (oFirstCard?.align as IColumn['align']) || 'left',
        width: oFirstCard?.width ?? (tableMode === 'streams' ? 560 : 680),
        features: articleFeatures,
        getValue: (record: Record<string, unknown>) => {
          if (tableMode === 'streams') {
            return String(record.stream_time ?? '') || '';
          }
          if (tableMode === 'posts') {
            return String(record.post_time ?? '') || '';
          }
          return String(record.date ?? '') || '';
        },
        render: (value, record, _rowIndex) => {
          const r = record as Record<string, unknown>;

          const coverUrl =
            tableMode === 'streams'
              ? String((r.channel_avatar as string) || (r.creator_avatar as string) || '')
              : tableMode === 'posts'
                ? (r.images as string) || ''
                : tableMode === 'videos'
                  ? (r.video_cover as string) || ''
                  : (r.cover as string) || '';
          const sourceLabel = String(
            r.creator ?? '',
          ).trim() || '-';
          const articleDate =
            tableMode === 'streams'
              ? formatPostTimeForDisplay(r.stream_time)
              : tableMode === 'posts'
                ? formatPostTimeForDisplay(r.post_time)
                : String(r.date ?? '');
          /** 卡片主文案：均优先 title；Articles 不用正文 text；Streams 已是 title */
          const articleText =
            tableMode === 'streams'
              ? String(r.title ?? '')
              : tableMode === 'posts'
                ? prCardPrimaryLine(r.title, r.message)
                : tableMode === 'videos'
                  ? prCardPrimaryLine(r.title, r.video_title)
                  : tableMode === 'articles'
                    ? String(r.title ?? '')
                    : String(r.text ?? '');
          const articleTitle = articleText;
          const articleUrl =
            tableMode === 'streams'
              ? String((r.channel_url as string) || (r.creator_url as string) || '')
              : (r.url as string) || '';
          const regionRaw = String(r.region ?? '').trim().toLowerCase();
          const flagUrl = regionRaw ? getCountryFlag(regionRaw) : null;
          const durationLabel =
            tableMode === 'streams'
              ? formatStreamDurationMinutes(r.duration)
              : tableMode === 'videos'
                ? formatStreamDurationSeconds(r.duration)
                : '';
          const showChannelIcon =
            tableMode === 'streams' ||
            tableMode === 'videos' ||
            tableMode === 'posts';
          const { img: channelIconUrl } = showChannelIcon
            ? channelInfo(String(r.channel ?? '') as never, undefined, false)
            : { img: '' };

          return (
            <Cell className="dbh-pr-media-table__article-cell" config={{ column: firstColumn }}>
              <div className="dbh-pr-media-table__article-card">
                <div
                  className={classNames('dbh-pr-media-table__article-cover-wrap', {
                    'dbh-pr-media-table__article-cover-wrap--stream': tableMode === 'streams',
                    'dbh-pr-media-table__article-cover-wrap--posts': tableMode === 'posts',
                  })}
                >
                  <BasicImage
                    className="dbh-pr-media-table__article-cover"
                    src={coverUrl}
                    errorSrc=""
                    alt={articleTitle}
                  />
                  {(tableMode === 'streams' || tableMode === 'videos') && durationLabel ? (
                    <span className="dbh-pr-media-table__cover-duration">{durationLabel}</span>
                  ) : null}
                </div>
                <div className="dbh-pr-media-table__article-body">
                  <div className="dbh-pr-media-table__article-meta">
                    <div className="dbh-pr-media-table__article-source-line">
                      {showChannelIcon && channelIconUrl ? (
                        <img
                          className="dbh-pr-media-table__channel-icon"
                          src={channelIconUrl}
                          alt=""
                        />
                      ) : null}
                      {flagUrl ? (
                        <img className="dbh-pr-media-table__article-flag" src={flagUrl} alt="" />
                      ) : null}
                      <span className="dbh-pr-media-table__article-source-name">{sourceLabel}</span>
                    </div>
                    <span className="dbh-pr-media-table__article-date">{articleDate}</span>
                  </div>
                  <div className="dbh-pr-media-table__article-content">
                    {articleText.trim() ? (
                      <TextBubbles
                        rowNum={tableMode === 'posts' ? 3 : 2}
                        placement="top"
                        depend={articleText}
                        className="dbh-pr-media-table__article-excerpt"
                        text={
                          cardLinkEnabled && articleUrl ? (
                            <p
                              className="dbh-pr-media-table__article-excerpt-link"
                              rel="noopener noreferrer"
                            >
                              {articleText}
                            </p>
                          ) : (
                            <span className="dbh-pr-media-table__article-excerpt-plain">{articleText}</span>
                          )
                        }
                        tipContent={(
                          <div className="dbh-pr-media-table__article-text-bubble">
                            {articleText}
                          </div>
                        )}
                        bubbleProps={{ overlayClassName: 'dbh-pr-media-table__article-bubble-overlay' }}
                      />
                    ) : (
                      <span className="dbh-pr-media-table__article-excerpt-plain">-</span>
                    )}
                  </div>
                </div>
              </div>
            </Cell>
          );
        },
      };
    } else if (isRegions) {
      const oRegion = ov.get('region');
      const { nameEn: rEn, nameCn: rCn } = takeLabelEnCn(
        { nameEn: 'Region', nameCn: '地区' },
        oRegion as any,
      );
      const regionFeatures = mergeFeatures(
        { sortable: true },
        oRegion?.features as Record<string, unknown> | undefined,
      ) as IColumn['features'];

      firstColumn = {
        code: 'region',
        name: rEn,
        nameEn: rEn,
        nameCn: rCn,
        align: (oRegion?.align as IColumn['align']) || 'left',
        width: oRegion?.width ?? 180,
        features: regionFeatures,
        render: (value, record, rowIndex) => {
          const ctx: PrMediaCellRenderContext = {
            value,
            record,
            rowIndex,
            isEN,
            channels,
            colMaxes: { ...colMaxes },
            formatMetric: (raw) => formatValueForRule(raw, channelValueFormat),
          };
          if (cellRenderers?.region) {
            return <Cell config={{ column: firstColumn }}>{cellRenderers.region(ctx)}</Cell>;
          }
          const r = record as Record<string, unknown>;
          const regionCode = String(r.region ?? '').toUpperCase();
          const regionName = isEN
            ? (String(r.region_en || r.original_region_en || r.region || '') || '-')
            : (String(r.region_zh || r.region_en || r.original_region_en || r.region || '') || '-');
          const flagUrl = regionCode ? getCountryFlag(regionCode.toLowerCase()) : null;

          return (
            <Cell
              className="dbh-pr-media-table__outlet-cell dbh-pr-media-table__outlet-cell--regions"
              config={{ column: firstColumn }}
            >
              <div className="dbh-pr-media-table__outlet-row dbh-pr-media-table__outlet-row--regions">
                {flagUrl ? (
                  <img className="dbh-pr-media-table__outlet-flag" src={flagUrl} alt="" style={{ width: 24, height: 18, borderRadius: 2 }} />
                ) : null}
                <span className="dbh-pr-media-table__outlet-name" style={{ fontWeight: 500 }}>{regionName}</span>
              </div>
            </Cell>
          );
        },
      };
    } else {
      const oOut = ov.get('media');
      const { nameEn: oEn, nameCn: oCn } = takeLabelEnCn(
        { nameEn: 'Media', nameCn: '媒体数' },
        oOut,
      );
      const outletFeatures = mergeFeatures(
        { sortable: true },
        oOut?.features as Record<string, unknown> | undefined,
      ) as IColumn['features'];

      firstColumn = {
        code: 'media',
        name: oEn,
        nameEn: oEn,
        nameCn: oCn,
        align: (oOut?.align as IColumn['align']) || 'left',
        width: oOut?.width ?? 220,
        features: outletFeatures,
        render: (value, record, rowIndex) => {
          const ctx: PrMediaCellRenderContext = {
            value,
            record,
            rowIndex,
            isEN,
            channels,
            colMaxes: { ...colMaxes },
            formatMetric: (raw) => formatValueForRule(raw, channelValueFormat),
          };
          if (cellRenderers?.media) {
            return <Cell config={{ column: firstColumn }}>{cellRenderers.media(ctx)}</Cell>;
          }
          const r = record as Record<string, unknown>;
          const { mediaText, flagUrl } = resolveMediaAndCountryFlag(r);
          const rawSecondary =
            (r.media as string) && String(r.media).trim() !== ''
              ? String(r.media)
              : value != null && value !== ''
                ? String(value)
                : '';
          const secondaryName =
            rawSecondary && rawSecondary !== String(r.media ?? '') ? rawSecondary : '';
          const logo = getLogoUrl(r);
          const platformRowsNew = normalizePlatformTotalsFromApi(r);
          const legacyByChannel =
            (r.engagement__by_channel as Record<string, number> | undefined) ||
            (r.audience__by_channel as Record<string, number> | undefined) ||
            {};
          const platformRows: TPlatformTotalRow[] =
            platformRowsNew.length > 0
              ? platformRowsNew
              : (() => {
                const legacy = legacyByChannel;
                const order =
                  channels.length > 0
                    ? (channels as string[]).filter((c) => legacy[c] != null)
                    : (Object.keys(legacy) as string[]);
                return order
                  .map((code) => ({ code, value: legacy[code] }))
                  .filter((row) => row.value != null && Number.isFinite(Number(row.value)));
              })();

          return (
            <Cell className="dbh-pr-media-table__outlet-cell" config={{ column: firstColumn }}>
              <div className="dbh-pr-media-table__outlet-row">
                <BasicImage
                  className="dbh-pr-media-table__outlet-avatar"
                  src={logo || CompanyDefault}
                  errorSrc={CompanyDefault}
                  alt=""
                />
                <div className="dbh-pr-media-table__outlet-text">
                  <div className="dbh-pr-media-table__country-line">
                    {flagUrl ? <img className="dbh-pr-media-table__outlet-flag" src={flagUrl} alt="" /> : null}
                    <span className="dbh-pr-media-table__country-text">{mediaText}</span>
                  </div>
                  {secondaryName ? (
                    <div className="dbh-pr-media-table__outlet-name-row">
                      <span className="dbh-pr-media-table__outlet-name">{secondaryName}</span>
                    </div>
                  ) : null}
                  {platformRows.length > 0 ? (
                    <div className="dbh-pr-media-table__channels-shell">
                      <TextBubbles
                        rowNum={2}
                        placement="top"
                        depend={platformRows.map((row) => `${row.code}:${row.value}`).join('|')}
                        className="dbh-pr-media-table__channels-preview"
                        text={(
                          <span className="dbh-pr-media-table__channels-wrap">
                            {platformRows.map((row, idx) => {
                              const { img: channelIconSrc } = channelInfo(String(row.code) as any, undefined, false);
                              return (
                                <span key={`${row.code}-${idx}`} className="dbh-pr-media-table__channels-chip">
                                  <img
                                    className="dbh-pr-media-table__channels-chip-icon"
                                    src={channelIconSrc}
                                    alt=""
                                  />
                                  <span className="dbh-pr-media-table__channel-val">
                                    {formatValueForRule(row.value, channelValueFormat)}
                                  </span>
                                </span>
                              );
                            })}
                          </span>
                        )}
                        tipContent={(
                          <div className="dbh-pr-media-table__channels-tip-scroll">
                            <ul className="dbh-pr-media-table__stacked-tooltip-list">
                              {platformRows.map((row, idx) => {
                                const { img: channelIconSrc, name: channelName } = channelInfo(
                                  String(row.code) as never,
                                  undefined,
                                  false,
                                );
                                return (
                                  <li key={`${row.code}-${idx}`} className="dbh-pr-media-table__stacked-tooltip-item">
                                    <img className="dbh-pr-media-table__stacked-tooltip-icon" src={channelIconSrc} alt="" />
                                    <span className="dbh-pr-media-table__stacked-tooltip-name">{channelName}</span>
                                    <span className="dbh-pr-media-table__stacked-tooltip-val">
                                      {formatValueForRule(row.value, channelValueFormat)}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                        bubbleProps={{ overlayClassName: 'dbh-pr-media-table__channel-bubble-overlay' }}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </Cell>
          );
        },
      };
    }

    const metricColumns: IColumn[] = buildPrMetricColumns({
      tableMode,
      isEN,
      channels,
      colMaxes,
      colorByCodeProp,
      cellRenderers,
      ov,
      activeMetricCodes,
      metricNameEnMap,
      metricNameCnMap,
      defaultMetricFeaturesBase,
      getColumnFeaturesExtra,
      engagementFeatureMerged: engagementFeatureMerged as IColumn['features'] & { format?: string; },
      setStackedBarTooltip,
    });

    return [rowIndexCol, firstColumn, ...metricColumns];
  }, [
    isEN,
    channels,
    colMaxes,
    cellRenderers,
    columnConfigFromPage,
    rowPage,
    pageSize,
    colorByCodeProp,
    tableMode,
    cardLinkEnabled,
    setStackedBarTooltip,
  ]);
}
